import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { resolveImageUrl } from '@/utils/imageUrl';

const NAV_LINKS = [
  { href: '#leistungen', label: 'Leistungen' },
  { href: '#velux', label: 'VELUX' },
  { href: '#ueber-uns', label: 'Über uns' },
  { href: '#referenzen', label: 'Referenzen' },
  { href: '#kontakt', label: 'Kontakt' },
];

export const Navbar = ({ settings }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isSolid = !isHome || scrolled;

  const scrollToSection = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isSolid ? 'bg-white shadow-lg' : 'bg-[#0077D4]/40 backdrop-blur-sm'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img
              src={resolveImageUrl(settings?.logo_url) || ''}
              alt={settings?.company_name || 'HKE Bedachung'}
              className="h-14 w-auto"
              style={{ display: resolveImageUrl(settings?.logo_url) ? 'block' : 'none' }}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map(link => (
              <a
                key={link.href}
                href={link.href}
                onClick={e => scrollToSection(e, link.href.replace('#', ''))}
                className={`font-medium text-sm uppercase tracking-wider transition-colors duration-300 ${
                  isSolid
                    ? 'text-slate-700 hover:text-[#0077D4]'
                    : 'text-white hover:text-white/70'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#kontakt"
              onClick={e => scrollToSection(e, 'kontakt')}
              className="bg-[#CC2229] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#a31b20] transition-all duration-300 inline-flex items-center gap-2"
              data-testid="navbar-cta"
            >
              <Phone size={16} /> Kontakt
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen
              ? <X size={28} className={isSolid ? 'text-slate-900' : 'text-white'} />
              : <Menu size={28} className={isSolid ? 'text-slate-900' : 'text-white'} />
            }
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0077D4] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-1">
            {NAV_LINKS.map(link => (
              <a
                key={link.href}
                href={link.href}
                onClick={e => scrollToSection(e, link.href.replace('#', ''))}
                className="block py-3 text-white font-medium text-lg border-b border-white/10"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#kontakt"
              onClick={e => scrollToSection(e, 'kontakt')}
              className="block mt-4 bg-[#CC2229] text-white text-center py-4 font-bold uppercase tracking-wider"
              data-testid="mobile-cta"
            >
              Anfrage senden
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
